import { useCallback, useState } from "react";

import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

// The phone that sits at this desk (ENG-2293). Per BROWSER, not per user, on
// purpose: the desk is the constant and the people rotate, so whoever signs
// in at the front-desk computer gets the right offer, and nobody is asked
// about the front desk's phone from their laptop at home. Written by a
// successful log-in from this browser; nothing about who did it is kept.
const STORAGE_KEY = "suno_softphone_desk_v1";
// "Not now" lasts the browser session, for the person who said it: at a
// shared computer the next person to sign in still gets the offer.
const DISMISSED_KEY = "suno_softphone_desk_prompt_dismissed_v1";

export type RememberedDesk = { deviceId: string; deviceName: string };

const storage = setupLocalStorageAPI(STORAGE_KEY);

const isRememberedDesk = (data: unknown): data is RememberedDesk =>
  !!data &&
  typeof (data as RememberedDesk).deviceId === "string" &&
  typeof (data as RememberedDesk).deviceName === "string";

export function readRememberedDesk(): RememberedDesk | null {
  const data = storage.getData(null, isRememberedDesk);
  return isRememberedDesk(data) ? data : null;
}

export function rememberDesk(desk: RememberedDesk) {
  storage.updateData(desk);
  try {
    sessionStorage.removeItem(DISMISSED_KEY);
  } catch {
    // sessionStorage unavailable; the prompt simply shows again.
  }
}

export function forgetDesk() {
  storage.clear();
}

const dismissalValue = (deviceId: string, userId: number | null) =>
  `${deviceId}:${userId ?? ""}`;

export function isPromptDismissed(
  deviceId: string,
  userId: number | null
): boolean {
  try {
    return (
      sessionStorage.getItem(DISMISSED_KEY) === dismissalValue(deviceId, userId)
    );
  } catch {
    return false;
  }
}

export function dismissPrompt(deviceId: string, userId: number | null) {
  try {
    sessionStorage.setItem(DISMISSED_KEY, dismissalValue(deviceId, userId));
  } catch {
    // Nothing to do: without sessionStorage the prompt shows on the next load.
  }
}

// The remembered desk as React state, so a take-over or a forget re-renders
// the walk-up prompt in the same session.
export default function useRememberedDesk() {
  const [desk, setDesk] = useState<RememberedDesk | null>(readRememberedDesk);
  const remember = useCallback((next: RememberedDesk) => {
    rememberDesk(next);
    setDesk(next);
  }, []);
  const forget = useCallback(() => {
    forgetDesk();
    setDesk(null);
  }, []);
  return { desk, remember, forget };
}
